<template>
  <div v-if="canAddProduct" class="pdp-variant-selector">
    <h3 class="pdp-variant-selector-heading">
      {{ product.type }}
    </h3>

    <div v-if="hasInventory" class="pdp-variant-selector-price-area">
      <div v-if="selectedVariant" class="pdp-variant-selector-price">
        {{ formattedPrice(variantPrice()) }}
      </div>
      <div v-else class="pdp-variant-selector-price">
        {{ formattedPrice(product.price) }}
      </div>

      <div class="pdp-variant-selector-unit-price">
        <span v-if="packOptions.length >= 1 && hasUnitDivisor">
          {{ formattedUnitPrice(unitDivisor) }}
          {{ product.unitPriceText }}
        </span>
      </div>

      <div class="pdp-variant-selector-compare">
        <span
          v-if="selectedVariant && selectedVariant.compareAtPrice"
          class="pdp-variant-selector-compare-text-price"
        >
          {{ translations.previousPriceText }}
          <strike>
            {{ formattedPrice(selectedVariant.compareAtPrice) }}
          </strike>
        </span>
        <span
          v-if="selectedVariant && selectedVariant.compareAtPrice"
          class="pdp-variant-selector-compare-saving"
        >
          -{{ formattedPrice(selectedVariant.compareAtPrice - variantPrice()) }}
        </span>
      </div>
    </div>

    <div v-else class="pdp-variant-selector-sold-out">
      {{ translations.soldOut }}
    </div>

    <template v-if="hasVariants">
      <div class="pdp-variant-selector-description">
        {{ sectionSettings.headerText }}
      </div>

      <div class="pdp-variant-selector-field">
        <RadioButtonGroup
          :options="packOptions"
          :selected="selected"
          @change="updateSelectedOption"
        />
      </div>

      <div class="pdp-variant-selector-description">
        <em>{{ retrieveCustomDescription }}</em>
      </div>
    </template>

    <div v-if="hasInventory" class="pdp-variant-selector-description">
      {{ translations.quantityText }}
    </div>
    <div v-if="hasInventory" class="pdp-variant-selector-field">
      <QuantitySelector
        v-model="quantity"
        :minimum="minimumQuantity"
        :inventory-quantity="inventoryQuantity"
        :inventory-policy-is-continue="
          selectedVariant.inventoryPolicyIsContinue
        "
        :selected="selected"
        @quantityUpdateEmit="updateQuantity"
      />
    </div>

    <div
      v-if="isSubscriptionProduct && hasInventory"
      class="purchase-type-selector-description"
    >
      {{ translations.frequencyText }}
    </div>

    <div
      v-if="isSubscriptionProduct && hasInventory"
      class="purchase-type-selector-field"
    >
      <PurchaseTypeSelector
        v-model="purchaseType"
        :discount-percentage="product.discountPercentage"
        :translations="translations"
        :selected-variant="selectedVariant"
        @purchaseTypeUpdateEmit="updatePurchaseType"
      />
      <SubscriptionIntervalSelector
        v-if="purchaseType === RECHARGE_PURCHASE_TYPES.SUBSCRIPTION"
        v-model="deliveryInterval"
        :delivery-interval="deliveryInterval"
        :shipping-interval-frequency="product.shippingIntervalFrequency"
        :shipping-interval-unit-type="product.shippingIntervalUnitType"
        :translations="translations"
        @deliveryIntervalUpdateEmit="updateDeliveryInterval"
      />
    </div>

    <template v-if="canAddProduct">
      <ButtonCTA
        :product="product"
        :selected-variant="selectedVariant"
        :quantity="quantity"
        :translations="translations"
        :variant-price="variantPrice()"
        :purchase-type="purchaseType"
        :delivery-interval="deliveryInterval"
        :shipping-interval-unit-type="product.shippingIntervalUnitType"
      />
    </template>

    <a
      v-if="canFindInStore"
      class="pdp-variant-selector-store-finder"
      :href="developerSettings.storeLink"
      v-html="storeFindButtonText"
    />
  </div>
</template>

<script>
import * as ShopifyCurrency from '@shopify/theme-currency';

import RadioButtonGroup from '../Inputs/RadioButtonGroup.vue';
import QuantitySelector from '../Inputs/QuantitySelector.vue';
import PurchaseTypeSelector from '../Inputs/PurchaseTypeSelector.vue';
import SubscriptionIntervalSelector from '../Inputs/SubscriptionIntervalSelector.vue';
import ButtonCTA from '../Inputs/ButtonCTA.vue';
import productCTAButtonsMixin from '../../mixins/productCTAButtonsMixin';
import { RECHARGE_PURCHASE_TYPES } from '../ProductCard/const.js';

export default {
  components: {
    RadioButtonGroup,
    QuantitySelector,
    ButtonCTA,
    PurchaseTypeSelector,
    SubscriptionIntervalSelector
  },
  mixins: [productCTAButtonsMixin],
  props: {
    product: {
      type: Object
    },
    translations: {
      type: Object
    },
    developerSettings: {
      type: Object
    },
    sectionSettings: {
      type: Object
    },
    metafields: {
      type: Object
    }
  },
  data() {
    return {
      selected:
        this.product.variants.length === 1
          ? this.product.title
          : this.product.variants[0].title, // @devs for a single product (variant), set selected to a default value. Else, the first variant.
      quantity: 1,
      purchaseType: RECHARGE_PURCHASE_TYPES.ONE_TIME,
      deliveryInterval: this.product.shippingIntervalFrequency[0]
    };
  },
  watch: {
    selected: {
      immediate: true,
      handler() {
        this.resetQuantity();
      }
    }
  },
  created() {
    this.RECHARGE_PURCHASE_TYPES = RECHARGE_PURCHASE_TYPES;
  },
  computed: {
    storeFindButtonText() {
      return this.translations.storeFind;
    },
    packOptions() {
      if (this.product.variants.length === 1) {
        return this.product.variants.map(variant => {
          return {
            isAvailable: variant.available,
            label: variant.title,
            value: variant.title,
            name: 'product'
          };
        });
      } else {
        return this.product.variants.map(variant => {
          return {
            isAvailable: variant.available,
            label: variant.options[0],
            value: variant.options[0],
            name: 'variant'
          };
        });
      }
    },
    selectedVariant() {
      // @devs For a single product/variant, we reduce the amount of clicking and just set the selectedVariant on page load
      if (this.product.variants.length === 1) {
        const variant = this.product.variants[0];
        variant.options[0] = variant.title; // @devs A single variant has "Default title" so we modify with the product title
        return variant;
      } else {
        return this.product.variants.find(variant => {
          return variant.options[0] === this.selected;
        });
      }
    },
    inventoryQuantity() {
      return this.selectedVariant ? this.selectedVariant.inventoryQuantity : 1;
    },
    minimumQuantity() {
      return 1;
    },
    getSelectedVariant() {
      if (!this.selectedVariant) {
        return false;
      }

      return this.product.variants.find(variant => {
        return variant.id === this.selectedVariant.id;
      });
    },
    unitDivisor() {
      if (this.packOptions.length < 1) {
        return false;
      }

      if (!this.selectedVariant || !this.selectedVariant.unitDivisor) {
        return this.product.unitDivisor;
      }

      return this.selectedVariant.unitDivisor;
    },
    retrieveCustomDescription() {
      if (this.hasInventory) {
        return this.selectedVariant
          ? this.getSelectedVariant.customSecondMessage
          : this.product.defaultSecondCustomText;
      }

      // @devs We don't allow the admin to select an out of stock product so we just load a default out of stock message irrespective of the variant
      return this.product.defaultOutOfStockText;
    },
    hasUnitDivisor() {
      return this.unitDivisor > 1;
    },
    hasInventory() {
      return this.selectedVariant.available;
    },
    hasVariants() {
      return this.product.variants.length > 1;
    },
    isSubscriptionProduct() {
      return this.product.hasSubscription === 'True';
    }
  },
  mounted() {
    if (this.product.variants.length === 1) {
      return;
    }

    const firstAvailableVariant = this.product.variants.find(variant => {
      return variant.available;
    });

    if (!firstAvailableVariant) {
      return;
    }

    this.selected = firstAvailableVariant.title;
  },
  methods: {
    updateQuantity(value) {
      return (this.quantity = value);
    },
    updateSelectedOption(value) {
      return (this.selected = value);
    },
    updatePurchaseType(value) {
      return (this.purchaseType = value);
    },
    updateDeliveryInterval(value) {
      return (this.deliveryInterval = value);
    },
    resetQuantity() {
      this.quantity = 1;
    },
    formattedUnitPrice(quantity = 1) {
      let price = this.variantPrice();

      return ShopifyCurrency.formatMoney(price / quantity, this.moneyFormat);
    },
    formattedPrice(price, quantity = 1) {
      return ShopifyCurrency.formatMoney(quantity * price, this.moneyFormat);
    },
    variantPrice() {
      if (!this.selectedVariant) {
        return this.product.price;
      }

      if (this.purchaseType === RECHARGE_PURCHASE_TYPES.ONE_TIME) {
        return this.selectedVariant.price;
      } else {
        return this.selectedVariant.subscriptionVariantPrice * 100;
      }
    }
  }
};
</script>
